import { createGlobalStyle } from 'styled-components';
import { FONTS, COLORS } from '../consts/style';

const Global = createGlobalStyle`
  html {
    box-sizing: border-box;
    scroll-behavior: smooth;
    overflow-x: hidden;
  }
  *, *:before, *:after {
    box-sizing: inherit;
  }

  body {
    font-family: ${FONTS.body};
    font-size: 16px;
    font-weight: 400;
    color: ${COLORS.text};
    background: ${COLORS.warmWhite};
    line-height: 1.7;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;
  }

  /* Custom cursor - dot (hidden) */
  .cursor-dot {
    display: none;
  }

  /* Custom cursor - dot */
  .cursor-dot {
    position: fixed;
    top: 0;
    left: 0;
    width: 6px;
    height: 6px;
    background: #fff;
    border-radius: 50%;
    pointer-events: none;
    z-index: 99999;
    transform: translate(-50%, -50%);
    transition: opacity 0.3s ease, width 0.3s ease, height 0.3s ease, background 0.3s ease;
  }

  /* Custom cursor - ring */
  .cursor-ring {
    position: fixed;
    top: 0;
    left: 0;
    width: 40px;
    height: 40px;
    border: 1.5px solid #fff;
    border-radius: 50%;
    pointer-events: none;
    z-index: 99998;
    transition: width 0.3s cubic-bezier(0.16, 1, 0.3, 1),
                height 0.3s cubic-bezier(0.16, 1, 0.3, 1),
                border-color 0.3s ease,
                background 0.3s ease,
                opacity 0.3s ease;
    transform: translate(-50%, -50%);
  }

  .cursor-hover .cursor-dot {
    width: 0;
    height: 0;
    opacity: 0;
  }
  .cursor-hover .cursor-ring {
    width: 60px;
    height: 60px;
    border-color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }

  .cursor-text .cursor-dot {
    width: 2px;
    height: 20px;
    border-radius: 1px;
    background: #fff;
  }
  .cursor-text .cursor-ring {
    width: 0;
    height: 0;
    opacity: 0;
  }

  .cursor-grab .cursor-dot {
    width: 0;
    height: 0;
    opacity: 0;
  }
  .cursor-grab .cursor-ring {
    width: 90px;
    height: 90px;
    border-color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
  .cursor-grab .cursor-ring::after {
    content: 'DRAG';
    font-family: ${FONTS.body};
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.18em;
    color: #fff;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  @media (hover: none), (pointer: coarse) {
    .cursor-dot, .cursor-ring { display: none; }
  }

  h1,h2,h3,h4,h5,h6 {
    font-family: ${FONTS.heading};
    font-weight: 700;
    color: ${COLORS.dark};
    line-height: 1.2;
  }

  a {
    text-decoration: none;
    transition: color 0.3s ease;
  }

  img {
    max-width: 100%;
  }

  *:focus {
    outline: none !important;
  }

  select {
    appearance: none;
    outline: none;
  }

  /* ---- Animations ---- */
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(24px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(-30px); }
    to { opacity: 1; transform: translateX(0); }
  }

  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(30px); }
    to { opacity: 1; transform: translateX(0); }
  }

  .animate-fadeInUp {
    animation: fadeInUp 0.6s ease forwards;
  }

  .animate-fadeIn {
    animation: fadeIn 0.8s ease forwards;
  }

  /* ---- Section label ---- */
  .section-label {
    display: inline-block;
    font-family: ${FONTS.body};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: ${COLORS.accent};
    margin-bottom: 12px;
  }

  /* ---- Section heading ---- */
  .section-heading {
    font-family: ${FONTS.heading};
    font-size: 2.5rem;
    font-weight: 300;
    color: ${COLORS.dark};
    line-height: 1.15;

    @media (max-width: 768px) {
      font-size: 1.8rem;
    }
  }

  .section-heading-light {
    font-family: ${FONTS.heading};
    font-size: 2.5rem;
    font-weight: 300;
    color: #fff;
    line-height: 1.15;

    @media (max-width: 768px) {
      font-size: 1.8rem;
    }
  }

  /* ---- Section subtitle ---- */
  .section-subtitle {
    font-size: 15px;
    color: ${COLORS.textMuted};
    line-height: 1.7;
    max-width: 560px;
  }

  /* ---- Button styles ---- */
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: ${FONTS.body};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 14px 28px;
    background: ${COLORS.accent};
    color: #fff;
    border: 1.5px solid ${COLORS.accent};
    border-radius: 0;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${COLORS.accentDark};
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      z-index: -1;
    }

    &:hover {
      color: #fff;
      border-color: ${COLORS.accentDark};
      &::before {
        transform: scaleX(1);
        transform-origin: left;
      }
    }

    svg {
      transition: transform 0.3s ease;
    }
    &:hover svg {
      transform: translateX(3px);
    }
  }

  .btn-outline {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: ${FONTS.body};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 14px 28px;
    background: transparent;
    color: rgba(255,255,255,0.85);
    border: 1px solid rgba(255,255,255,0.3);
    border-radius: 0;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: #fff;
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      z-index: -1;
    }

    &:hover {
      color: ${COLORS.dark};
      border-color: #fff;
      &::before {
        transform: scaleX(1);
        transform-origin: left;
      }
    }

    svg {
      transition: transform 0.3s ease;
    }
    &:hover svg {
      transform: translateX(3px);
    }
  }

  .btn-dark {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: ${FONTS.body};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 14px 28px;
    background: transparent;
    color: ${COLORS.dark};
    border: 1px solid ${COLORS.dark};
    border-radius: 0;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: color 0.35s ease;
    z-index: 1;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: ${COLORS.dark};
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
      z-index: -1;
    }

    &:hover {
      color: #fff;
      &::before {
        transform: scaleX(1);
        transform-origin: left;
      }
    }

    svg {
      transition: transform 0.3s ease;
    }
    &:hover svg {
      transform: translateX(3px);
    }
  }

  /* ---- Layout containers ---- */
  .pl-container {
    @media (min-width: 640px) { padding-left: calc( (100vw - 640px) / 2 ); }
    @media (min-width: 768px) { padding-left: calc( (100vw - 768px) / 2 ); }
    @media (min-width: 1024px) { padding-left: calc( (100vw - 1024px) / 2 ); }
    @media (min-width: 1280px) { padding-left: calc( (100vw - 1280px) / 2 ); }
  }

  /* ---- Social icons ---- */
  .social-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    transition: all 0.3s ease;
    &:hover {
      background: ${COLORS.accent}15;
      svg { fill: ${COLORS.accent}; }
    }
    svg { fill: ${COLORS.textLight}; width: 16px; height: 16px; }
  }

  .social-link-invert {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    transition: all 0.3s ease;
    &:hover {
      background: rgba(255,255,255,0.1);
      svg { fill: #fff; }
    }
    svg { fill: rgba(255,255,255,0.4); width: 16px; height: 16px; }
  }

  .social-brand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #1378e5;
    transition: transform 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
    svg { width: 18px; height: 18px; fill: #fff; }

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 18px rgba(0, 0, 0, 0.22);
      filter: brightness(1.06);
    }

    &.whatsapp { background: #25d366; }
    &.facebook { background: #1877f2; }
    &.instagram {
      background: radial-gradient(
        circle at 30% 110%,
        #fdf497 0%,
        #fdf497 5%,
        #fd5949 45%,
        #d6249f 60%,
        #285aeb 90%
      );
    }
  }

  .richText p {
    margin-top: 1em;
    color: ${COLORS.textMuted};
    line-height: 1.7;
  }

  /* ---- Brand name two-tone (DECORZO orange / SPACE blue) ---- */
  .brand-decorzo {
    color: ${COLORS.accent};
  }
  .brand-space {
    color: ${COLORS.blue};
  }

  ::selection {
    background: ${COLORS.accent};
    color: white;
  }

  .slick-dots li button:before {
    font-size: 8px;
  }
`;

export default Global;
